import { useEffect, useMemo, useRef, useState } from "react";
import type { AvailableModel } from "@/api/settings";
import { groupModelsByFamily } from "@/lib/modelSort";

/** Searchable model picker: a select-styled button opening a popover with
 *  an inline filter and family-grouped options. Replaces native <select>
 *  wherever the model list is long (59 OpenCode models and counting).
 *  Value "" = Auto. */
export function ModelCombobox({
  value,
  onChange,
  models,
  size = "md",
  autoHint,
}: {
  value: string;
  onChange: (v: string) => void;
  /** Already provider-filtered. */
  models: AvailableModel[];
  size?: "sm" | "md";
  /** Optional hint shown next to the Auto entry (resolved model). */
  autoHint?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    // Focus the filter as soon as the popover mounts.
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return models;
    return models.filter(
      (m) => m.id.toLowerCase().includes(q) || m.label.toLowerCase().includes(q),
    );
  }, [models, query]);
  const groups = useMemo(() => groupModelsByFamily(filtered), [filtered]);
  const autoVisible =
    !query.trim() || "auto".includes(query.trim().toLowerCase());

  const current = models.find((m) => m.id === value);
  const buttonText = value === "" ? "Auto" : (current?.label ?? value);

  const pick = (v: string) => {
    onChange(v);
    setOpen(false);
  };

  const sm = size === "sm";
  return (
    <div ref={rootRef} className={`relative ${sm ? "w-[11rem]" : "w-full min-w-0"}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex w-full items-center justify-between gap-1 truncate rounded-${sm ? "md" : "lg"} border border-line ${sm ? "bg-surface-2 px-1.5 py-0.5 text-xs text-ink-2" : "bg-surface px-3 py-2 text-sm text-ink"} text-left focus:border-line-strong focus:outline-none`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate">{buttonText}</span>
        <svg className={`${sm ? "h-3 w-3" : "h-4 w-4"} shrink-0 text-ink-3`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className={`absolute z-50 mt-1 max-h-72 w-full min-w-[14rem] overflow-y-auto rounded-lg border border-line bg-surface shadow-lg ${sm ? "bottom-full mb-1 mt-0" : ""}`}>
          <div className="sticky top-0 border-b border-line bg-surface p-1.5">
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  const first = filtered[0];
                  if (first) pick(first.id);
                  else if (autoVisible) pick("");
                }
              }}
              placeholder="Search models…"
              className="w-full rounded-md border border-line bg-surface-2 px-2 py-1 text-xs text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
            />
          </div>
          <ul role="listbox" className="p-1">
            {autoVisible && (
              <li
                role="option"
                aria-selected={value === ""}
                onClick={() => pick("")}
                className={`flex cursor-pointer items-baseline justify-between gap-2 rounded-md px-2 py-1 text-xs ${value === "" ? "bg-accent-soft text-accent" : "text-ink-2 hover:bg-surface-3"}`}
              >
                <span>Auto</span>
                {autoHint && <span className="truncate text-[10px] text-ink-3">{autoHint}</span>}
              </li>
            )}
            {groups.map((g) => (
              <li key={g.family}>
                <div className="mt-1 rounded-md bg-surface-3 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-ink first:mt-0">
                  {g.family}
                </div>
                <ul>
                  {g.models.map((m) => (
                    <li
                      key={m.id}
                      role="option"
                      aria-selected={m.id === value}
                      onClick={() => pick(m.id)}
                      className={`cursor-pointer truncate rounded-md px-2 py-1 text-xs ${m.id === value ? "bg-accent-soft text-accent" : "text-ink-2 hover:bg-surface-3"}`}
                      title={m.id}
                    >
                      {m.label}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
            {!autoVisible && groups.length === 0 && (
              <li className="px-2 py-2 text-xs text-ink-3">No models match.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
